import getQuoteTotalApi from '@/api/get/dealer/get-quote-total';
import Card from '@/components/card/card-component';
import Loader from '@/components/loader/loader-component';
import QuotePrice from '@/components/quote-price/quote-price-component';
import Input from '@/components/react-hook-form-inputs/input/input-component';
import { useLazyQuery } from '@/hooks/use-api';
import { APP_CONTEXT } from '@/utils/context';
import { SubheaderText } from '@/utils/global-fonts';
import toCurrency from '@/utils/to-currency';
import { requiredValidationMessage } from '@/utils/validations';
import { useCallback, useContext, useEffect } from 'react';
import { useFieldArray, useFormContext } from 'react-hook-form';
import { FiPercent } from 'react-icons/fi';
import { useRouteLoaderData } from 'react-router-dom';
import AccordionInput from '@/components/react-hook-form-inputs/accordion-input/accordion-input.component';
import Styled from './quote-price-form-style';

/**
 * Price form for the Quote Summary page - Price tab.
 * @file QuotePriceForm.jsx
 * @returns {JSX.Element} The rendered QuotePriceForm component.
 */
const QuotePriceForm = () => {
  const { refetch, data, loading } = useLazyQuery(getQuoteTotalApi);
  const routerData = useRouteLoaderData('main-summary');
  const { quoteResponseData } = routerData;
  const { selectedCurrency } = useContext(APP_CONTEXT);
  const selectedSymbol = selectedCurrency?.symbol || 'R';
  const { watch, reset, register } = useFormContext();
  const { fields: quotePrices, update } = useFieldArray({
    name: 'quote_modules_prices',
  });

  const quantity = watch('quote.quantity') || 1;
  const discount = watch('quote.discount') || 0;
  const quoteModuleValues = watch('quote_modules_prices');

  const overridePrice = watch('quote.override_unit_price');
  const unitPrice = watch('quote.unit_price');

  const handleInputChange = useCallback(
    (event) => {
      const { name, value } = event.target;
      update(name, value);
    },
    [update],
  );

  const sumPricesAndMultiply = (arr) => {
    const sum = arr?.reduce(
      (total, item) => total + parseFloat(item.price ?? 0),
      0,
    );
    return sum * quantity;
  };

  const qMT = sumPricesAndMultiply(quoteModuleValues) || 0;

  let totalUnitPrice = data?.aboveTheLineItem ?? 0;
  if (overridePrice) {
    totalUnitPrice = Number(unitPrice);
  }

  const totalATL = quantity * totalUnitPrice * (1 - parseFloat(discount) / 100);

  const totalString = toCurrency(
    totalATL + qMT,
    selectedCurrency?.name ?? 'ZAR',
  );

  useEffect(() => {
    refetch(quoteResponseData?.data?.quote?.id);
  }, [reset, refetch, quoteResponseData]);

  return (
    <>
      <Card margin="8px 0px 8px 0px" borderRadius="4px">
        <SubheaderText>Discount</SubheaderText>
        <Styled.GreyLine />
        <Styled.QuotePriceContainer>
          <Styled.PriceContainer>
            <Input
              label="Discount"
              placeholder=""
              name="quote.discount"
              iconRight={<FiPercent />}
              type="number"
              registerOptions={{
                onChange: handleInputChange,
              }}
            />
          </Styled.PriceContainer>
          <Styled.MessageContainer>
            <Input
              label="Quantity"
              placeholder="Quantity"
              name="quote.quantity"
              type="number"
              register={{
                required: requiredValidationMessage,
                onChange: handleInputChange,
              }}
            />
          </Styled.MessageContainer>
        </Styled.QuotePriceContainer>
        <Styled.MessageContainer>
          <Input
            label="Discount Comment"
            placeholder="Discount Comment"
            name="quote.discount_comment"
            register={{
              onChange: handleInputChange,
            }}
          />
        </Styled.MessageContainer>
      </Card>
      {quotePrices?.map((module, index) => (
        <QuotePrice
          key={module?.id}
          name={`quote_modules_prices.${index}`}
          heading={module?.title}
          selectedSymbol={selectedSymbol}
          onPriceChange={handleInputChange}
          onMessageChange={handleInputChange}
          register={register}
        />
      ))}
      <Card borderRadius="4px">
        <AccordionInput
          accordionName="quote.override_unit_price"
          accordionLabel="Override Price"
          inputName="quote.unit_price"
          inputLabel="Price"
          inputType="number"
          selectedSymbol={selectedSymbol}
          registerOptions={{
            valueAsNumber: true,
          }}
        />
      </Card>
      <Styled.StyledCard>
        <SubheaderText>Price Summary</SubheaderText>
        <Styled.PriceSummaryContainer>
          {loading ? (
            <Loader loading />
          ) : (
            <Styled.StyledColumn>
              <Styled.StyledRow>
                <span> Unit excl. VAT</span>
                <span>
                  {toCurrency(totalUnitPrice, selectedCurrency?.name ?? 'ZAR')}
                </span>
              </Styled.StyledRow>
              {quoteModuleValues?.map((module) => (
                <Styled.StyledRow>
                  <span> {module?.title}</span>
                  <span>
                    {selectedSymbol} {module.price}
                  </span>
                </Styled.StyledRow>
              ))}
              <Styled.StyledRow>
                <span> Quantity</span>
                <span> {quantity || 0} </span>
              </Styled.StyledRow>
              <Styled.StyledRow>
                <span> Discount</span>
                <span> {discount || 0} %</span>
              </Styled.StyledRow>
              <Styled.StyledRow>
                <span> Total excl. VAT</span>
                <span>{totalString}</span>
              </Styled.StyledRow>
            </Styled.StyledColumn>
          )}
        </Styled.PriceSummaryContainer>
      </Styled.StyledCard>
    </>
  );
};

export default QuotePriceForm;
